import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { useCallback, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { DateRange } from 'dashboard/components-lib/DashboardDatePicker/DashboardDatePicker';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { BulkSetCaseStateData } from 'dashboard/forms/FormBulkSetCaseState';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Case } from 'dashboard/models/case';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplateStatus } from 'dashboard/models/case-template';
import { Team } from 'dashboard/models/team';
import { User } from 'dashboard/models/user';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  CaseQueueCasesFilterParams,
  clearCaseQueue,
  clearCasesFilters,
  FilterValues,
  getInitialQueueCasesFilterState,
  setCaseQueue,
  setCasesCurrentPage,
  setCasesFilters,
  setCasesPageSize,
} from 'dashboard/redux/slices/case-queue';
import { RootState } from 'dashboard/redux/store';
import { loadCaseQueue, loadCaseQueueCases } from 'dashboard/redux/thunk-actions/case-queue';
import { loadCaseTemplateBasicsOptions } from 'dashboard/redux/thunk-actions/case-templates';
import { loadTeams } from 'dashboard/redux/thunk-actions/teams';
import { loadUserList } from 'dashboard/redux/thunk-actions/user';
import DashboardCaseQueuesShowView from 'dashboard/views/DashboardCaseQueuesShowView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import usePermission from 'lib/hooks/usePermission';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { getDateRange, updateDateFilters } from 'lib/utils/filters';

type Params = { caseQueueId: string };

const DashboardCaseQueuesShowRoute = (props: PropsFromRedux) => {
  useOnMountAndUnmount(props.clearCaseQueue);

  const { caseQueueId } = useParams<Params>() as Params; // caseQueueId is always defined
  const hasUserViewPermission = usePermission(Permission.UserView);
  const hasTeamViewPermission = usePermission(Permission.TeamView);

  const { loading: loadingCaseQueue } = useApiLoader({
    loader: () => props.loadCaseQueue(caseQueueId),
    deps: [props.loadCaseQueue, caseQueueId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        switch (error.status) {
          case 401:
            props.showMessage(
              'Your role does not have permissions to view case queues in this organization.',
              MessageType.Notice,
            );
            break;
          default:
            props.showMessage('Failed to load case queue.', MessageType.Failure);
        }
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: queueCasesLoader, loading: loadingCases } = useApiLoader({
    loader: () =>
      props.loadCaseQueueCases({
        currentEnvironment: props.currentEnvironment,
        queueId: caseQueueId,
        caseFilters: props.casesFilters,
        currentPage: props.casesCurrentPage,
        pageSize: props.casesPageSize,
      }),
    deps: [
      props.loadCaseQueueCases,
      caseQueueId,
      props.currentEnvironment,
      props.casesFilters,
      props.casesCurrentPage,
      props.casesPageSize,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        switch (error.status) {
          case 401:
            props.showMessage(
              'Your role does not have permissions to view case queues in this organization.',
              MessageType.Notice,
            );
            break;
          default:
            props.showMessage('Failed to load case queue.', MessageType.Failure);
        }
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: props.loadCaseTemplateBasicsOptions,
    deps: [props.loadCaseTemplateBasicsOptions],
    onError: () => {},
    onNetworkError: () => {},
  });

  useApiLoader({
    loader: () => props.loadUserList(),
    deps: [props.loadUserList],
    skip: !hasUserViewPermission,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load users.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadTeams(),
    deps: [props.loadTeams],
    skip: !hasTeamViewPermission,
    onError: () => {
      props.showMessage('Failed to load teams.', MessageType.Failure);
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: updateCaseQueueNameError, callback: updateCaseQueueName } = useApiCallback(
    async (name: string) => {
      const { data: caseQueue } = await SessionClientDashboard.updateResource<CaseQueue>({
        id: props.caseQueue?.id ?? '',
        attributes: {
          name,
        },
        type: ApiModel.CaseQueue,
      });

      props.setCaseQueue({ caseQueue });
      props.showMessage('Successfully renamed case queue.', MessageType.Success, true);
    },
    [props.caseQueue],
  );

  useApiErrorHandler({
    error: updateCaseQueueNameError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to rename case queue, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { handlePageLinkClick, handlePageSizeClick, handleOmniSearch } = useTable({
    pageSize: props.casesPageSize,
    currentPage: props.casesCurrentPage,
    setFilters: (filters) => props.setCasesFilters({ filters: filters }),
    setCurrentPage: (currentPage) => props.setCasesCurrentPage({ currentPage: currentPage }),
    setPageSize: (pageSize) => props.setCasesPageSize({ pageSize: pageSize }),
  });

  const handleAssigneeFilterUpdate = (
    userAssigneeIds: FilterValues,
    teamAssigneeIds: FilterValues,
  ) => {
    props.setCasesFilters({ filters: { assigneeIds: userAssigneeIds, teamAssigneeIds } });
  };

  const handleDateUpdate = useCallback((range: DateRange) => {
    const { start, end } = getDateRange(range.startDate, range.endDate);
    props.setCasesFilters({
      filters: {
        startDate: start,
        endDate: end,
      },
    });
  }, []);

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      updateDateFilters(newDateRange, handleDateUpdate);
    },
    [handleDateUpdate],
  );

  const handleClearFilters = () => {
    props.clearCasesFilters();
  };

  const { error: bulkAssignCasesError, callback: handleBulkAssign } = useApiCallback(
    async (caseIds: string[], userId: string, message: string) => {
      // If we are passed an undefined userId, we take that to mean unassigned.
      // To prevent axios from stripping that from the payload, we need to set it to null
      // See https://github.com/axios/axios/issues/1139
      const assigneeId = userId != null ? userId : null;
      await SessionClientDashboard.postResourceSingletonAction<Case>(
        { type: ApiModel.Case },
        'bulk-assign',
        {
          meta: {
            'assignee-id': assigneeId,
            'case-ids': caseIds,
            message: message,
          },
        },
      );

      queueCasesLoader();
      const displayMessage =
        caseIds.length > 50
          ? 'Successfully started assigning cases in the background.'
          : caseIds.length > 1
            ? 'Successfully assigned Cases.'
            : 'Successfully assigned Case.';
      props.showMessage(displayMessage, MessageType.Success);
    },
    [queueCasesLoader],
  );

  useApiErrorHandler({
    error: bulkAssignCasesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to assign, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to assign, please try again.', MessageType.Failure, false),
  });

  const { error: bulkSetStateError, callback: handleBulkSetState } = useApiCallback(
    async (caseIds: string[], data: BulkSetCaseStateData) => {
      await SessionClientDashboard.postResourceSingletonAction<Case>(
        { type: ApiModel.Case },
        'bulk-set-state',
        {
          meta: {
            'case-ids': caseIds,
            state_id: data.stateId,
            message: data.message ?? '',
          },
        },
      );

      queueCasesLoader();
      const displayMessage =
        caseIds.length > 100
          ? 'Successfully started updating cases status in the background.'
          : caseIds.length > 1
            ? 'Successfully updated case statuses.'
            : 'Successfully updated case status.';
      props.showMessage(displayMessage, MessageType.Success);
    },
    [queueCasesLoader],
  );

  useApiErrorHandler({
    error: bulkSetStateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 403) {
        props.showMessage(
          'Failed to update case status. Please complete the checklist first.',
          MessageType.Failure,
        );
      } else {
        props.showMessage(
          'Failed to start updating case statuses, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  // Initially reload the cases filters state startDate and endDate
  useEffect(() => {
    const initialFilters: CaseQueueCasesFilterParams = getInitialQueueCasesFilterState();
    props.setCasesFilters({
      filters: initialFilters,
    });
  }, [props.setCasesFilters]);

  if (loadingCaseQueue) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  } else if (props.caseQueue == null) {
    return (
      <Backdrop>
        <NullState message='We couldn’t find that Case Queue.' backRoute='/dashboard/case-queues' />
      </Backdrop>
    );
  }

  return (
    <DashboardCaseQueuesShowView
      activeCaseTemplateBasicsOptions={props.activeCaseTemplateBasicsOptions}
      caseQueue={props.caseQueue}
      cases={props.cases}
      users={props.users.filter((user: User) => user.attributes.status === 'active')}
      teams={props.teams.filter((team: Team) => team.attributes.status === 'active')}
      casesFilters={props.casesFilters}
      loadingCases={loadingCases}
      pageSize={props.casesPageSize}
      paginationParams={props.casesPaginationParams}
      handleAssigneeFilterUpdate={handleAssigneeFilterUpdate}
      handleBulkAssign={handleBulkAssign}
      handleBulkSetState={handleBulkSetState}
      handleDateUpdate={handleDateUpdate}
      handleDateUpdateV2={handleDateUpdateV2}
      handleOmniSearch={handleOmniSearch}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleClearFilters={handleClearFilters}
      updateCaseQueueName={updateCaseQueueName}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    activeCaseTemplateBasicsOptions: state.caseTemplates.basicsOptions.filter(
      (option) => option.status === CaseTemplateStatus.Active,
    ),
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    caseQueue: state.caseQueue.caseQueue,
    casesCurrentPage: state.caseQueue.cases.tableState.currentPage,
    casesPageSize: state.caseQueue.cases.tableState.pageSize,
    casesPaginationParams: state.caseQueue.cases.tableState.paginationParams,
    casesFilters: state.caseQueue.cases.tableState.filters,
    cases: state.caseQueue.cases.cases,
    users: state.user.users,
    teams: state.teams.teams,
  };
};

const mapDispatchToProps = {
  loadCaseTemplateBasicsOptions,
  loadCaseQueue,
  loadCaseQueueCases,
  clearCaseQueue,
  loadUserList,
  loadTeams,
  setCasesCurrentPage,
  setCasesPageSize,
  setCasesFilters,
  setCaseQueue,
  clearCasesFilters,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardCaseQueuesShowRoute);
